<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || resourceLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !resourceLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12">
          <h4 class="secondary300--text">{{ $t('public-contact') }}</h4>
        </v-col>
      </v-row>
      <v-row
        v-for="(publicContact, i) in resource.publicContacts"
        :key="'public-contact-' + i"
        class="mb-2"
      >
        <v-col
          cols="12"
          id="verify-email"
          class="primary400--text pt-4 pb-4"
          v-if="publicContact.id && !publicContact.verified"
        >
          <v-row>
            <v-col cols="9" class="d-flex flex-row justify-left align-center">
              <b>The user has not been verified.</b>
            </v-col>
            <v-col cols="3" class="d-flex flex-row justify-end">
              <v-btn
                depressed
                rounded
                @click="resendVerification(publicContact)"
                outlined
                class="primary200--text font-weight-bold"
                style="background: white"
                data-cy="submit"
              >
                {{ 'Resend Verification' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="10" md="6">
          <label>{{ $t('email') }}</label>
          <p class="label-info">
            {{ $t('service-message') }}
          </p>
          <div class="d-flex flex-row justify-center">
            <v-text-field
              v-model="publicContact.email"
              :placeholder="$t('email')"
              outlined
              type="email"
              :disabled="!edit"
              class="required"
              :rules="emailRules()"
              data-cy="email"
              v-on:keydown.enter.prevent
              clearable
            ></v-text-field>
            <v-btn v-if="!next && edit" fab small text @click="removePublicContact(i)" class="ml-2">
              <v-icon>
                $vuetify.icons.trash
              </v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col class="pt-0" cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('firstname') }}</label>
                    <p class="label-info">
                      First Name of the service's contact person to be displayed publicly at the
                      Portal.
                    </p>
                    <v-text-field
                      v-model="publicContact.firstname"
                      :placeholder="$t('firstname')"
                      outlined
                      :disabled="!edit"
                      data-cy="firstName"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('lastname') }}</label>
                    <p class="label-info">
                      Last Name of the service's contact person to be displayed publicly at the
                      Portal.
                    </p>
                    <v-text-field
                      v-model="publicContact.lastname"
                      :placeholder="$t('lastname')"
                      outlined
                      :disabled="!edit"
                      data-cy="lastName"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('phone') }}</label>
                    <p class="label-info">
                      Telephone of the service's contact person to be displayed publicly at the
                      Portal.
                    </p>
                    <v-text-field
                      v-model="publicContact.phone"
                      :placeholder="$t('phone')"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="phone"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('position') }}</label>
                    <p class="label-info">
                      Position of the service's contact person to be displayed publicly at the
                      Portal.
                    </p>
                    <v-text-field
                      v-model="publicContact.position"
                      :placeholder="$t('position')"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="main-contact-position"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('Organisation') }}</label>
                    <p class="label-info">{{ $t('hosting-institution-message') }}</p>
                    <v-autocomplete
                      v-model="publicContact.institution"
                      :items="institutions"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      :placeholder="'Select Organisation'"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="hostingInstitution"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row class="pt-0 links" v-if="!next && edit">
        <v-col cols="12">
          <v-btn
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
            @click="addPublicContact()"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-public-contact') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" v-if="!next">
          <h4 class="secondary300--text">{{ $t('other-contact') }}</h4>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ 'Helpdesk Email' }}</label>
                    <p class="label-info">
                      The email to ask more information from the Provider about this service.
                    </p>
                    <v-text-field
                      v-model="resource.helpDeskEmail"
                      outlined
                      clearable
                      :disabled="!edit"
                      :placeholder="'Helpdesk Email'"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next">
      <v-col cols="6" class="d-flex justify-end">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="back()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          v-if="isEquipment"
          depressed
          rounded
          x-large
          color="primary200"
          @click="add()"
          :disabled="!valid || !resource.publicContacts.length"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
        <v-btn
          v-else
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('submit') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="my-5" v-if="!next && edit" justify="end">
      <v-btn outlined class="black--text font-weight-bold mx-2" rounded x-large @click="cancel()">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        class="white--text font-weight-bold"
        @click="add()"
        :disabled="!valid || !resource.publicContacts.length"
        >{{ $t('update') }}
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'ResourceContactInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String],
      default: null,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
    isEquipment: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -1,
      unsavedChanges: false,
      resource: {
        id: null,
        publicContacts: [],
        mainContacts: [],
        helpDeskEmail: null,
        securityContactEmail: null,
      },
      institutions: [],
      resourceLoading: true,
    };
  },
  mounted() {
    this.axios
      .get('/api/v1/hosting-institutions/all')
      .then(res => (this.institutions = res.data))
      .finally(() => {
        this.loadingComponents++;
      });
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
      this.unsavedChanges = false;
    },
    back() {
      console.log('back!');
      this.$emit('tab', 2);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/resources/contact-info', this.resource)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('resource-updated'),
          });
          this.unsavedChanges = false;
          if (!this.isEquipment && this.resource.draft) {
            this.$router.push({
              name: 'Services',
            });
          }
          this.$emit('tab', 4);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    resendVerification(publicContact) {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/resources/resend-verification', { id: publicContact.id })
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: 'Verification sent it successfully',
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    addPublicContact() {
      this.resource.publicContacts.push({
        email: null,
        firstname: null,
        lastname: null,
        phone: null,
        position: null,
        institution: null,
      });
    },
    removePublicContact(index) {
      this.resource.publicContacts.splice(index, 1);
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      if (value === false) {
        console.log('Loading');
        if (this.id != null) {
          this.axios
            .get('/api/v1/resources/' + this.id)
            .then(res => {
              this.resource = res.data;
              if (!this.resource.publicContacts.length) {
                this.addPublicContact();
              }
              this.$nextTick(() => {
                this.unsavedChanges = false;
              });
              this.$nextTick(() => {
                this.resourceLoading = false;
              });
            })
            .catch(error => {
              console.log({ error });
            });
        } else {
          this.resourceLoading = false;
        }
      }
    },
    resource: {
      handler: function(val, oldVal) {
        console.log('[Resource] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
